import posthog, { type CapturedNetworkRequest } from 'posthog-js';
import { Endpoint } from '@/Config/api/endpoint';
import { measureOpenAiConversion } from '@/Config/libs/openAiPixel';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { dropNoiseExceptions } from '@/Domain/helper/posthogExceptionFilter';

const OAUTH_TRACKING_KEY = 'posthog_oauth_tracking';

/**
 * Responses that carry a credential, and must never reach session recording.
 *
 * The WhatsApp link response contains a live linking code and the `wa.me` URL
 * built around it. Masking the DOM is not enough: network capture reads the
 * response before anything renders, and someone reading a recording inside the
 * code's 15 minutes could bind their own WhatsApp number to that account.
 */
const CREDENTIAL_BEARING_ENDPOINTS = [Endpoint.channelIdentity.whatsappLink];

const maskCredentialBearingResponses = (
  request: CapturedNetworkRequest,
): CapturedNetworkRequest => {
  if (!CREDENTIAL_BEARING_ENDPOINTS.some((path) => request.name?.includes(path))) {
    return request;
  }
  return { ...request, requestBody: undefined, responseBody: '[redacted]' };
};

export function initPostHog(): void {
  if (!import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    return;
  }

  posthog.register({ 'web-type': 'app' });
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    enable_heatmaps: true,
    capture_pageview: false,
    capture_pageleave: true,
    capture_heatmaps: true,
    capture_dead_clicks: true,
    capture_exceptions: true,
    capture_performance: false,
    advanced_disable_feature_flags_on_first_load: true,
    before_send: dropNoiseExceptions,
    session_recording: {
      // Body capture is disabled client-side, not merely masked: a custom mask
      // replaces posthog-js's built-in payload scrubbing, so if response-body
      // recording were remotely enabled, every response NOT on our endpoint
      // list (JWT mint/refresh included) would pass through unscrubbed. With
      // recording off at the client the remote toggle cannot reopen that, and
      // the mask below stays as the belt for request names/URLs.
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: maskCredentialBearingResponses,
    },
  });

  posthog.debug(false);

  // Expose posthog to window for debugging (feature flag overrides, etc.)
  window.posthog = posthog;

  processOAuthTracking();
}

function processOAuthTracking(): void {
  const oauthData = localStorage.getItem(OAUTH_TRACKING_KEY);
  if (!oauthData) return;

  try {
    const { variant, provider, timestamp, is_registration } = JSON.parse(oauthData);
    // Only process if less than 5 minutes old (avoid stale data)
    if (Date.now() - timestamp < 5 * 60 * 1000) {
      posthog.capture(PostHogEventEnum.OAuthCompleted, {
        background_variant: variant,
        provider: provider,
      });

      if (is_registration) {
        const regProps = { event_category: 'User Registration', auth_method: provider };
        const dl = (window.dataLayer = window.dataLayer || []);
        dl.push({ event: 'account_created', ...regProps });
        const hsq = (window._hsq = window._hsq || []);
        hsq.push(['trackCustomBehavioralEvent', { name: 'account_created', properties: regProps }]);
        measureOpenAiConversion('registration_completed');
      }

      const loginProps = { event_category: 'User login', auth_method: provider };
      const dl = (window.dataLayer = window.dataLayer || []);
      dl.push({ event: 'login_form_success', ...loginProps });
      const hsq = (window._hsq = window._hsq || []);
      hsq.push([
        'trackCustomBehavioralEvent',
        { name: 'login_form_success', properties: loginProps },
      ]);
    }
  } catch (e) {
    console.error('Failed to parse OAuth tracking data', e);
  }
  localStorage.removeItem(OAUTH_TRACKING_KEY);
}
